import React, { useEffect } from 'react';

const VideoModal = ({ 
  isOpen, 
  onClose, 
  article,
  sectionTitle = ""
}) => {
  // Cerrar modal con tecla Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Función para detectar el tipo de video
  const getVideoType = (videoUrl, videoType) => {
    if (!videoUrl) return null;
    
    // Verificar si es MP4 directo (Bunny CDN u otros)
    if (videoType === 'mp4' || videoUrl.includes('.mp4') || videoUrl.includes('bunny')) {
      return 'direct';
    }
    
    // Verificar si es YouTube
    if (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be')) {
      return 'youtube';
    }
    
    // Verificar si es Vimeo
    if (videoUrl.includes('vimeo.com')) {
      return 'vimeo';
    }
    
    return 'embed'; // Por defecto, tratar como embed
  };

  // Detectar si estamos en Capacitor iOS
  const isCapacitorIOS = window.Capacitor?.getPlatform?.() === 'ios' || 
    window.location.origin === 'capacitor://localhost';

  // Función para obtener el embed URL del video
  const getEmbedUrl = (videoUrl) => {
    if (!videoUrl) return null;

    // Extraer YouTube ID de cualquier formato
    let youtubeId = null;
    const ytMatch = videoUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube-nocookie\.com\/embed\/)([^&\n?#]+)/);
    if (ytMatch) youtubeId = ytMatch[1];

    if (youtubeId) {
      if (isCapacitorIOS) {
        // iOS Capacitor: usar proxy page (origin capacitor:// no es válido para YouTube)
        return `https://www.appagustinos.es/youtube-embed.html?v=${youtubeId}`;
      }
      // Android/PWA: iframe directo a YouTube
      return `https://www.youtube.com/embed/${youtubeId}?playsinline=1&rel=0&modestbranding=1`;
    }

    // Vimeo (funciona en todas las plataformas)
    if (videoUrl.includes('vimeo.com')) {
      const videoId = videoUrl.match(/vimeo\.com\/(?:.*\/)?(\d+)/);
      if (videoId) {
        return `https://player.vimeo.com/video/${videoId[1]}?playsinline=1&color=e62c19`;
      }
    }

    return videoUrl;
  };

  const getSectionIcon = (sectionTitle) => {
    const icons = {
      'Evangelio': '📖',
      'Buenos días': '🌅', 
      'Clave agustiniana': '🔑',
      'Santos Agustinos': '👼',
      'Vocaciones': '✨',
      'Vamos al cole': '🎓',
      'Te interesa': '🌍',
      'Está pasando': '📰',
      'Píldoras de San Agustín': '💊',
      'Habla San Agustín': '🗣️',
      'Orando con San Agustín': '�',
      'Historia y personajes': '📚',
      'Sabías que…': '📚',
      'Reflexión agustiniana': '🤔',
      'Domingo con San Agustín': '☀️'
    };
    return icons[sectionTitle] || '🎥';
  };

  if (!isOpen || !article) return null;

  const videoType = getVideoType(article.videoUrl, article.videoType);
  const embedUrl = getEmbedUrl(article.videoUrl);
  const isDirectVideo = videoType === 'direct';

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        backdropFilter: 'blur(4px)'
      }}
      onClick={onClose}
    >
      <div 
        className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full mx-4 overflow-hidden"
        style={{
          maxHeight: '90vh',
          animation: 'modalSlideIn 0.3s ease-out'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full bg-black bg-opacity-50 backdrop-blur-sm text-white hover:bg-opacity-70 transition-all duration-200 flex items-center justify-center"
          style={{
            fontSize: '18px',
            lineHeight: '1'
          }}
        >
          ×
        </button>

        <div className="relative" style={{ marginBottom: '20px' }}>
          {article.videoUrl ? (
            isDirectVideo ? (
              // Video MP4 directo (Bunny CDN)
              <div className="relative w-full bg-black rounded-t-2xl overflow-hidden">
                <video 
                  width="100%" 
                  controls 
                  autoPlay
                  playsInline
                  className="w-full"
                  style={{ maxHeight: '60vh' }}
                  poster={article.videoThumbnail || article.imageUrl || article.image}
                >
                  <source src={article.videoUrl} type="video/mp4" />
                  Tu navegador no soporta la reproducción de video.
                </video>
              </div>
            ) : (
              // YouTube o Vimeo (iframe) - Carga inmediata sin delay
              <div className="relative w-full" style={{ paddingBottom: '56.25%', overflow: 'hidden', backgroundColor: '#000' }}>
                <iframe
                  src={embedUrl}
                  title={article.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
                  allowFullScreen
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    backgroundColor: '#000'
                  }}
                />
              </div>
            )
          ) : (
            <div className="w-full h-64 bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center">
              <div className="text-center">
                <div className="text-6xl mb-4">🎥</div>
                <p className="text-gray-600">No se puede reproducir este video</p>
                <a 
                  href={article.videoUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-red-600 hover:underline mt-2 inline-block"
                >
                  Ver en sitio original
                </a>
              </div>
            </div>
          )}
        </div>

        <div className="p-7 mt-4">
          <div className="mb-6">
            {sectionTitle && (
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm text-red-600 font-semibold bg-red-50 px-3 py-1 rounded-full">
                  {getSectionIcon(sectionTitle)} {sectionTitle}
                </span>
              </div>
            )}

            <h2 className="text-lg font-bold text-gray-800 mb-2 leading-tight">
              {article.title}
            </h2>

            {article.description && (
              <p className="text-sm text-gray-600 leading-relaxed mb-3">
                {article.description}
              </p>
            )}

            <div className="flex items-center gap-3 text-xs text-gray-500">
              {article.date && (
                <span className="flex items-center gap-1">
                  📅 {new Date(article.date).toLocaleDateString('es-ES', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric'
                  })}
                </span>
              )}

              <span className="flex items-center gap-1 bg-blue-50 px-2 py-1 rounded-full text-blue-600 font-medium">
                🎥 Video
              </span>

              {article.author && article.author !== 'Agustinos' && (
                <span className="flex items-center gap-1">
                  ✏️ {article.author}
                </span>
              )}
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-gray-100">
            <button
              onClick={() => {
                onClose();
                window.dispatchEvent(new CustomEvent('openArticleFromVideo', {
                  detail: { article, sectionTitle }
                }));
              }}
              className="w-full py-3 px-4 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl font-semibold hover:from-red-600 hover:to-red-700 transition-all duration-200 flex items-center justify-center gap-2"
            >
              📖 Ver artículo completo
            </button>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: scale(0.9) translateY(20px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default VideoModal;